import { Observable, throwError } from 'rxjs';

// ERROR CLASS
// ==========

/**
 * A tweaked Error for storing responses.
 *
 * @prop {any} cause  The original response object.
 */
export class LumResponseError extends Error {}

// TYPES, INTERFACES
// ==========

export interface IAsLumResponseErrorOptions {
  /** @default false */
  suppressConsoleError ?:boolean;
}

// UTILS
// ==========

/**
 * Converts a response to a {@link LumResponseError}, using the fallback message
 * if the response does not have a valid error message.
 *
 * @param {string} fallbackMessage - The fallback error message to use if the
 *   response does not have a valid error message.
 * @param {IAsLumResponseErrorOptions} [options] - Optional settings.
 * @param {boolean} [options.suppressConsoleError=false] - If `true`, suppresses
 *   the console error logging.
 * @returns {LumResponseError} - The normalized response error.
 *
 * @example
 * fooApiService.get().pipe(
 *   catchError((response :any) => {
 *     const error = asResponseError(response, fallbackMessage, options);
 *
 *     return throwError(error);
 *   },
 * );
 */
export function asLumResponseError(
  response :any,
  fallbackMessage :string,
  options ?:IAsLumResponseErrorOptions,
) :LumResponseError {
  options = {
    suppressConsoleError: false,
    ...options,
  };

  const message :string =
    response?.error?.message ??
    response?.error?.error ??
    response?.message ??
    response?.error;

  if (!options.suppressConsoleError) {
    const serverMessageForConsole = message ? JSON.stringify(message) : 'none';

    console.error(
      `LumResponseError: Server error (message: ${serverMessageForConsole})`,
    );
  }

  const stringMessage = typeof message === 'string' ? message : fallbackMessage;

  return new LumResponseError(stringMessage, { cause: response });
}

/**
 * Builds a function that throws a {@link LumResponseError} from a `response`
 * param. It uses the `fallbackMessage` if the response does not have a valid
 * error with a non-empty message.
 *
 * @param {string} fallbackMessage - The fallback error message to use if the
 *   response does not have a valid error message.
 * @param {IAsLumResponseErrorOptions} [options] - Optional settings.
 * @param {boolean} [options.suppressConsoleError=false] - If `true`, suppresses
 *   the console error logging.
 * @returns {(response: any) => Observable<never>} - A function that takes a
 *   response and throws a `ResponseError` with a message.
 *
 * @example
 * fooApiService.get().pipe(
 *   catchError(throwAsResponseError('Failed to fetch stocks')),
 * );
 */
export function throwResponseAsLumResponseError(
  fallbackMessage :string,
  options ?:IAsLumResponseErrorOptions,
) :(response :any) => Observable<never> {
  options = {
    // suppress by default, since `throwError` will print to the console if it's
    // not handled.
    suppressConsoleError: true,
    ...options,
  };

  return (response :any) =>
    throwError(() => asLumResponseError(response, fallbackMessage, options));
}
